import dayjs from "dayjs";

const parseJsonField = (value: unknown): Record<string, any> => {
  if (!value) return {};
  if (typeof value === "object") return value as Record<string, any>;
  try {
    return JSON.parse(String(value));
  } catch {
    return {};
  }
};

/** Match treatment-plan procedure id/code against procedures master. */
export const findProcedureInMaster = (
  procedures: any[] = [],
  procedureRef: any,
) => {
  const list = Array.isArray(procedures)
    ? procedures
    : Array.isArray((procedures as any)?.data)
      ? (procedures as any)?.data
      : Array.isArray((procedures as any)?.content)
        ? (procedures as any)?.content
        : [];
  if (!list?.length || !procedureRef) return null;
  const id = String(
    procedureRef?.id ??
      procedureRef?.procedurecode ??
      procedureRef?.procedureCode ??
      procedureRef ??
      "",
  );
  if (!id) return null;
  return (
    list.find(
      (p: any) =>
        String(p?.procedurecode) === id ||
        String(p?.procedureCode) === id ||
        String(p?.procedureid) === id ||
        String(p?.id) === id,
    ) || null
  );
};

const dedupeByKey = (items: any[], getKey: (item: any) => string) => {
  const map = new Map<string, any>();
  (items || []).forEach((item, index) => {
    const key = getKey(item) || `idx-${index}`;
    if (!map.has(key)) map.set(key, item);
  });
  return Array.from(map.values());
};

/** Normalize procedure nested list fields (array | {data|content}). */
const asList = (value: unknown): any[] => {
  if (Array.isArray(value)) return value;
  if (Array.isArray((value as any)?.data)) return (value as any).data;
  if (Array.isArray((value as any)?.content)) return (value as any).content;
  return [];
};

const getProcedureImplants = (masterOrPlan: any): any[] =>
  asList(
    masterOrPlan?.implants ??
      masterOrPlan?.implantsList ??
      masterOrPlan?.implantList ??
      masterOrPlan?.Implants,
  );

/**
 * Union specialities / investigations / implants from all selected
 * treatment-plan procedures (supports multi-procedure + Get/portal restore).
 */
export const deriveOptionsFromTreatmentPlans = (
  treatmentPlans: any[] = [],
  proceduresMaster: any[] = [],
) => {
  const specialities: any[] = [];
  const investigations: any[] = [];
  const implants: any[] = [];

  (treatmentPlans || []).forEach((plan) => {
    const master =
      findProcedureInMaster(proceduresMaster, plan?.procedure) ||
      findProcedureInMaster(proceduresMaster, plan?.procedureCode) ||
      findProcedureInMaster(proceduresMaster, plan?.externalProcedureCode);

    const specs = asList(master?.specialities ?? plan?.specialities);
    const invs = asList(
      master?.investigations ??
        plan?.investigations ??
        plan?.procedure?.investigations,
    );
    const masterImplants = getProcedureImplants(master);
    const planImplants = getProcedureImplants(plan);
    const procedureImplants = getProcedureImplants(plan?.procedure);
    const implantSource =
      masterImplants.length > 0
        ? masterImplants
        : planImplants.length > 0
          ? planImplants
          : procedureImplants;

    specs.forEach((s: any) => specialities.push(s));
    invs.forEach((i: any) => investigations.push(i));
    implantSource.forEach((i: any) => implants.push(i));
  });

  return {
    specialities: dedupeByKey(
      specialities,
      (s) => String(s?.specialityid ?? s?.id ?? s?.specialitycode ?? ""),
    ),
    investigations: dedupeByKey(
      investigations,
      (i) =>
        String(
          i?.investigationid ??
            i?.investigationId ??
            i?.investigationcode ??
            i?.investigationCode ??
            i?.id ??
            "",
        ),
    ),
    implants: dedupeByKey(
      implants,
      (i) =>
        String(
          i?.implantsid ??
            i?.implantid ??
            i?.implantId ??
            i?.implantscode ??
            i?.implantcode ??
            i?.implantCode ??
            i?.id ??
            "",
        ),
    ),
  };
};

export const mapInvestigationsToDocTypes = (investigations: any[] = []) =>
  (investigations || []).map((item) => {
    const id =
      item?.investigationid ??
      item?.investigationId ??
      item?.documentMasterId ??
      item?.id;
    const name =
      item?.investigation ??
      item?.investigationName ??
      item?.label ??
      "";
    const code =
      item?.investigationcode ??
      item?.investigationCode ??
      item?.documentUniqueCode ??
      "";
    return {
      id,
      label: name && code ? `${name} (${code})` : name || code,
      name,
      labelId: id,
      documentUniqueCode: code,
    };
  });

export const mapImplantsToDocTypes = (implants: any[] = []) =>
  (implants || []).map((item, index) => {
    const id =
      item?.implantsid ??
      item?.implantid ??
      item?.implantId ??
      item?.implantscode ??
      item?.implantcode ??
      item?.implantCode ??
      item?.documentMasterId ??
      item?.id ??
      `implant-${index}`;
    const name =
      item?.implants ??
      item?.implant ??
      item?.implantName ??
      item?.implantname ??
      item?.name ??
      item?.label ??
      "";
    const code =
      item?.implantscode ??
      item?.implantcode ??
      item?.implantCode ??
      item?.implantcodehbp20 ??
      item?.documentUniqueCode ??
      "";
    const amount =
      item?.amount ??
      item?.implantamount ??
      item?.implantAmount ??
      item?.implantsamount ??
      item?.implantcost ??
      item?.implantCost ??
      item?.price ??
      item?.rate ??
      null;
    const maxImplantsAllowed = Number(
      item?.maximplantsallowed ??
        item?.maxImplantsAllowed ??
        item?.MaxImplantsAllowed ??
        item?.maximplantallowed ??
        item?.maxImplantAllowed ??
        item?.quantityallowed ??
        item?.quantityAllowed ??
        0,
    );
    return {
      id,
      label: name && code ? `${name} (${code})` : name || code || String(id),
      name: name || String(id),
      labelId: id,
      documentUniqueCode: code,
      code,
      amount,
      maxImplantsAllowed:
        Number.isFinite(maxImplantsAllowed) && maxImplantsAllowed > 0
          ? maxImplantsAllowed
          : null,
    };
  });

const formatDateField = (value: unknown, dateFormat: string) =>
  value ? dayjs(value as dayjs.ConfigType).format(dateFormat) : null;

const splitTildeField = (value: unknown) => {
  const parts = String(value ?? "").split("~");
  return { code: parts[0] || "", name: parts[1] || parts[0] || "" };
};

/** Form `items` (procedure rows) → payload `treatmentPlansData`. */
export const mapFormItemsToTreatmentPlansData = (
  items: any[] = [],
  dateFormat = "DD-MM-YYYY HH:mm:ss",
): any[] => {
  return (items || []).map((item) => {
    const procParsed = parseJsonField(item?.procedureCode);
    const stratParsed = parseJsonField(item?.stratOptionValue);

    const procedureCode =
      procParsed.procedureCode ??
      item?.externalProcedureCode ??
      procParsed.id;

    const stratification =
      item?.stratOptionValue && Object.keys(stratParsed).length
        ? {
            id:
              stratParsed.stratificationCode ??
              stratParsed.stratificationid ??
              stratParsed.id ??
              null,
            name:
              stratParsed.stratificationName ?? stratParsed.name ?? null,
          }
        : {};

    const referral = splitTildeField(item?.procedureReferral);
    const accreditation = splitTildeField(item?.accreditationStatus);
    const treatingDoctor = splitTildeField(item?.treatingDoctor);

    return {
      speciality: item?.speciality || {},
      procedure: {
        id: procedureCode,
        name: procParsed.procedureName ?? item?.procedureName ?? "",
        procedureName: item?.unspecifiedProcedureName ?? null,
        procedureAmount:
          item?.unspecifiedProcedureAmount ?? item?.procedureAmount ?? null,
      },
      stratification,
      numberOfDaysPerUnits:
        item?.numberOfDays != null ? Number(item.numberOfDays) : null,
      treatmentStartDate: formatDateField(item?.treatmentStartDate, dateFormat),
      externalProcedureCode: item?.externalProcedureCode ?? procedureCode,
      procedureAmount: item?.procedureAmount,
      unspecifiedProcedureName: item?.unspecifiedProcedureName,
      unspecifiedProcedureAmount: item?.unspecifiedProcedureAmount,
      isAmountAutoFilled: item?.isAmountAutoFilled,
      accreditationStatus: accreditation.code,
      accreditationStatusName: accreditation.name,
      procedureReferral: referral.code,
      procedureReferralName: referral.name,
      treatingDoctorCode: treatingDoctor.code,
      treatingDoctor: treatingDoctor.name,
      procedureCode,
      procedureName: procParsed.procedureName,
      strats: procParsed.strats,
      procedureid: procParsed.procedureid ?? procParsed.id,
      stratOptionValue: item?.stratOptionValue,
      ...procParsed,
      ...(Object.keys(stratParsed).length ? stratParsed : {}),
    };
  });
};

/** Saved `treatmentPlansData` → form `items` for AyushmanProcedure. */
export const mapTreatmentPlansDataToFormItems = (
  treatmentPlansData: any[] = [],
  proceduresMaster: any[] = [],
  dateFormat = "DD-MM-YYYY HH:mm:ss",
): any[] => {
  return (treatmentPlansData || []).map((tp) => {
    const proc = tp?.procedure || {};
    const code =
      proc?.id ?? tp?.procedureCode ?? tp?.externalProcedureCode ?? "";
    const master = proceduresMaster?.find(
      (p) => String(p?.procedurecode) === String(code),
    );

    const procedureCodeJson = JSON.stringify({
      procedureCode: code,
      procedureName:
        proc?.name ?? tp?.procedureName ?? master?.procedurename ?? "",
      strats:
        tp?.strats ??
        master?.isstratificationapplicable ??
        proc?.strats,
      id: Number(
        tp?.procedureid ?? master?.procedureid ?? master?.id ?? proc?.id,
      ),
      implants: master?.implantsList || master?.implants || [],
      implantApplicable: master?.isimplantapplicable,
      specialities: master?.specialities || [],
      investigations: master?.investigations || [],
      specialityType: tp?.specialityType,
    });

    let stratOptionValue: string | undefined;
    if (tp?.stratOptionValue) {
      stratOptionValue =
        typeof tp.stratOptionValue === "string"
          ? tp.stratOptionValue
          : JSON.stringify(tp.stratOptionValue);
    } else if (
      tp?.stratification?.id ||
      tp?.stratification?.name ||
      tp?.stratificationCode
    ) {
      stratOptionValue = JSON.stringify({
        stratificationCode:
          tp?.stratification?.id ?? tp?.stratificationCode,
        stratificationName:
          tp?.stratification?.name ?? tp?.stratificationName,
      });
    } else if (tp?.stratificationCode || tp?.stratificationName) {
      stratOptionValue = JSON.stringify({
        stratificationCode: tp.stratificationCode,
        stratificationName: tp.stratificationName,
      });
    }

    const joinTilde = (code?: string, name?: string) =>
      code && name && code !== name ? `${code}~${name}` : code || name || "";

    return {
      procedureCode: procedureCodeJson,
      externalProcedureCode: tp?.externalProcedureCode ?? code,
      numberOfDays: String(
        tp?.numberOfDaysPerUnits ?? tp?.numberOfDays ?? "1",
      ),
      treatmentStartDate: tp?.treatmentStartDate
        ? dayjs(tp.treatmentStartDate, dateFormat)
        : null,
      procedureAmount: tp?.procedureAmount ?? proc?.procedureAmount,
      unspecifiedProcedureName:
        tp?.unspecifiedProcedureName ?? proc?.procedureName,
      unspecifiedProcedureAmount:
        tp?.unspecifiedProcedureAmount ?? proc?.procedureAmount,
      isAmountAutoFilled: tp?.isAmountAutoFilled,
      stratOptionValue,
      accreditationStatus: joinTilde(
        tp?.accreditationStatus,
        tp?.accreditationStatusName,
      ),
      procedureReferral: joinTilde(
        tp?.procedureReferral,
        tp?.procedureReferralName,
      ),
      treatingDoctor: joinTilde(tp?.treatingDoctorCode, tp?.treatingDoctor),
    };
  });
};
